'use client';
import { useState, useRef } from 'react';
import Link from 'next/link';
import { ShareBar } from '@/components/shared/ShareBar';
import { trackLead } from '@/lib/analytics';

function EmailCapture({ category, label }: { category: string; label: string }) {
  const [email, setEmail] = useState('');
  const [done, setDone] = useState(false);
  const [consent, setConsent] = useState(false);
  function submit(e: React.FormEvent) {
    e.preventDefault();
    if (!email || !consent) return;
    fetch('/api/newsletter-signup', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ email, category, source: 'kalkulator' }),
    }).catch(() => {});
    trackLead('generate_lead', { content_name: category, method: 'kalkulator' });
    setDone(true);
  }
  if (done) return (
    <div className="bg-green-50 border border-green-200 rounded-2xl p-5 text-center mb-6">
      <div className="text-green-800 font-medium mb-1">Takk! Du er påmeldt.</div>
      <div className="text-green-600 text-sm">Du vil motta tips om {label.toLowerCase()} på e-post.</div>
    </div>
  );
  return (
    <div className="bg-stone-50 border border-stone-200 rounded-2xl p-5 mb-6">
      <h3 className="font-semibold text-stone-900 mb-1">Få gratis tips om {label.toLowerCase()}</h3>
      <p className="text-stone-500 text-sm mb-4">Vi sender deg evidensbaserte tips tilpasset ditt mål — maks 1 gang per uke.</p>
      <form onSubmit={submit} className="space-y-3">
        <input type="email" placeholder="din@epost.no" value={email} onChange={e => setEmail(e.target.value)}
          className="w-full border border-stone-200 rounded-xl px-4 py-3 text-sm focus:outline-none focus:border-stone-400" />
        <label className="flex items-start gap-2 cursor-pointer">
          <input type="checkbox" checked={consent} onChange={e => setConsent(e.target.checked)} className="mt-0.5 flex-shrink-0" />
          <span className="text-xs text-stone-500">Jeg samtykker til å motta markedsføring fra CoachGuiden per e-post. Du kan melde deg av når som helst.</span>
        </label>
        <button type="submit" disabled={!email || !consent} className="w-full py-2.5 bg-stone-900 text-white rounded-xl text-sm font-medium hover:bg-stone-800 disabled:opacity-40 transition-colors">
          Meld meg på gratis tips
        </button>
      </form>
    </div>
  );
}


const QUESTIONS = [
  { q: 'Jeg føler meg overveldet av arbeidsoppgaver', opts: ['Sjelden','Av og til','Ofte','Nesten alltid'] },
  { q: 'Jeg sover dårlig på grunn av bekymringer', opts: ['Sjelden','Av og til','Ofte','Nesten alltid'] },
  { q: 'Jeg mangler energi selv etter en fridag', opts: ['Sjelden','Av og til','Ofte','Nesten alltid'] },
  { q: 'Det er vanskelig å koble av etter jobb', opts: ['Sjelden','Av og til','Ofte','Nesten alltid'] },
  { q: 'Jeg føler meg emosjonelt utladet', opts: ['Sjelden','Av og til','Ofte','Nesten alltid'] },
  { q: 'Jeg er irritabel eller kortlunta uten grunn', opts: ['Sjelden','Av og til','Ofte','Nesten alltid'] },
  { q: 'Jeg synes det er vanskelig å konsentrere meg', opts: ['Sjelden','Av og til','Ofte','Nesten alltid'] },
  { q: 'Jeg føler at ingenting nytter eller motiverer meg', opts: ['Sjelden','Av og til','Ofte','Nesten alltid'] },
  { q: 'Jeg har fysiske symptomer (hodepine, mage, spenninger)', opts: ['Sjelden','Av og til','Ofte','Nesten alltid'] },
  { q: 'Jeg utsetter oppgaver jeg normalt ville gjort greit', opts: ['Sjelden','Av og til','Ofte','Nesten alltid'] },
];

function Stresstest() {
  const [answers, setAnswers] = useState<Record<number,number>>({});
  const [done, setDone] = useState(false);
  const resultRef = useRef<HTMLDivElement>(null);

  const score = Object.values(answers).reduce((s,v) => s+v, 0);
  const max = QUESTIONS.length * 3;
  const pct = Math.round((score / max) * 100);

  const result =
    pct < 25 ? { label: 'Lavt stressnivå', desc: 'Du håndterer hverdagen bra. Fortsett å prioritere hvile og egenomsorg.', color: 'text-green-700', bg: 'bg-green-50 border-green-200', btn: false }
    : pct < 50 ? { label: 'Moderat stress', desc: 'Du kjenner på stress jevnlig. Noen enkle tiltak kan hjelpe deg holde balansen.', color: 'text-amber-700', bg: 'bg-amber-50 border-amber-200', btn: false }
    : pct < 75 ? { label: 'Høyt stress', desc: 'Stressnivået er høyt og påvirker sannsynligvis jobb, søvn og relasjoner. Det er på tide å gjøre noe.', color: 'text-orange-700', bg: 'bg-orange-50 border-orange-200', btn: true }
    : { label: 'Fare for utbrenthet', desc: 'Du viser tegn på utbrenthet. Det er viktig å søke støtte og gjøre endringer nå, ikke senere.', color: 'text-red-700', bg: 'bg-red-50 border-red-200', btn: true };

  function handleSubmit() {
    setDone(true);
    // Scroll to top of result after state update
    setTimeout(() => {
      resultRef.current?.scrollIntoView({ behavior: 'smooth', block: 'start' });
    }, 50);
  }

  if (done) return (
    <div ref={resultRef} className="space-y-5 scroll-mt-20">
      {/* Result badge */}
      <div className={`border rounded-2xl p-8 text-center ${result.bg}`}>
        <div className="text-xs font-semibold uppercase tracking-widest text-stone-400 mb-2">Ditt resultat</div>
        <div className={`text-3xl font-semibold mb-2 ${result.color}`}>{result.label}</div>
        <div className="text-stone-600 text-sm mb-5 max-w-sm mx-auto leading-relaxed">{result.desc}</div>
        <div className="flex items-center gap-3 max-w-xs mx-auto">
          <div className="flex-1 h-3 bg-white rounded-full overflow-hidden border border-stone-200">
            <div className={`h-full rounded-full transition-all ${pct < 25 ? 'bg-green-500' : pct < 50 ? 'bg-amber-500' : pct < 75 ? 'bg-orange-500' : 'bg-red-500'}`} style={{width:`${pct}%`}}/>
          </div>
          <span className={`text-sm font-semibold ${result.color}`}>{pct}%</span>
        </div>
      </div>

      {/* CTA — shown for ALL scores, more urgent for high scores */}
      <div className="bg-stone-900 text-white rounded-2xl p-8 text-center">
        <div className="text-stone-400 text-xs uppercase tracking-widest mb-3">Neste steg</div>
        {result.btn ? (
          <>
            <h2 className="font-bold text-2xl mb-2">Stress forsvinner ikke av seg selv.</h2>
            <p className="text-stone-300 text-base mb-1">En mental helse coach gir deg <span className="text-white font-semibold">konkrete verktøy du kan bruke i morgen</span> —</p>
            <p className="text-stone-400 text-sm mb-6">ikke generelle råd du allerede har hørt.</p>
          </>
        ) : (
          <>
            <h2 className="font-bold text-2xl mb-2">Vil du holde stressnivået lavt?</h2>
            <p className="text-stone-300 text-base mb-1">En coach hjelper deg bygge <span className="text-white font-semibold">vaner som holder deg i balanse</span> —</p>
            <p className="text-stone-400 text-sm mb-6">før stresset øker igjen.</p>
          </>
        )}
        <Link href="/mental-helse" className="inline-block px-6 py-3 bg-white text-stone-900 rounded-xl text-sm font-semibold hover:bg-stone-100 transition-colors">
          Finn mental helse coach gratis →
        </Link>
      </div>

      {pct >= 50 && (
        <div className="bg-white border border-stone-200 rounded-2xl p-5">
          <h3 className="font-semibold text-stone-900 mb-3">3 konkrete tiltak du kan gjøre nå</h3>
          <div className="space-y-2">
            {[
              ['Sett grenser','Si nei til én ting denne uken som ikke er absolutt nødvendig.'],
              ['Beveg deg','20 min daglig gange senker kortisol merkbart innen 2 uker.'],
              ['Snakk med noen','En coach, kollega eller lege — del det du bærer på.'],
            ].map(([t,d]: any) => (
              <div key={t} className="flex gap-3 bg-stone-50 rounded-xl p-3">
                <span className="text-green-500 flex-shrink-0">✓</span>
                <div><div className="text-sm font-medium text-stone-900">{t}</div><div className="text-xs text-stone-500">{d}</div></div>
              </div>
            ))}
          </div>
        </div>
      )}

      <EmailCapture category="mental-helse" label="Stress og mental helse" />

      <button onClick={() => {setAnswers({});setDone(false);}} className="w-full py-2.5 border border-stone-200 rounded-xl text-sm text-stone-500 hover:bg-stone-50 transition-colors">
        Ta testen på nytt
      </button>
    </div>
  );

  const answered = Object.keys(answers).length;

  return (
    <div className="space-y-4">
      <div className="bg-white border border-stone-200 rounded-xl px-4 py-2.5 flex items-center justify-between">
        <span className="text-sm text-stone-500">{answered} av {QUESTIONS.length} besvart</span>
        <div className="w-32 h-1.5 bg-stone-100 rounded-full overflow-hidden">
          <div className="h-full bg-stone-900 rounded-full transition-all" style={{width:`${(answered/QUESTIONS.length)*100}%`}}/>
        </div>
      </div>

      <div className="space-y-3">
        {QUESTIONS.map((item, i) => (
          <div key={i} className="bg-white border border-stone-200 rounded-2xl p-4">
            <div className="text-sm font-medium text-stone-900 mb-3">{i+1}. {item.q}</div>
            <div className="grid grid-cols-2 gap-2">
              {item.opts.map((opt, j) => (
                <button key={opt} onClick={() => setAnswers(p => ({...p,[i]:j}))}
                  className={`py-2 px-3 rounded-xl text-xs font-medium border transition-all ${answers[i]===j?'border-stone-900 bg-stone-900 text-white':'border-stone-200 text-stone-600 hover:bg-stone-50'}`}>
                  {opt}
                </button>
              ))}
            </div>
          </div>
        ))}
      </div>

      <button disabled={answered < QUESTIONS.length} onClick={handleSubmit}
        className="w-full py-3.5 bg-stone-900 text-white rounded-xl font-medium text-sm disabled:opacity-30 hover:bg-stone-800 transition-colors">
        {answered < QUESTIONS.length ? `Besvar alle ${QUESTIONS.length} spørsmål (${QUESTIONS.length - answered} igjen)` : 'Se resultatet →'}
      </button>
    </div>
  );
}

export default function StresstestPage() {
  return (
    <div className="min-h-screen gradient-warm">
      <header className="glass border-b border-stone-200/50 sticky top-0 z-10 shadow-sm">
        <div className="max-w-4xl mx-auto px-4 h-14 flex items-center justify-between">
          <Link href="/" className="flex items-center gap-2 font-semibold text-stone-900">
            <span className="w-7 h-7 rounded-lg bg-stone-900 flex items-center justify-center"><svg viewBox="0 0 32 32" fill="none" className="w-5 h-5"><circle cx="16" cy="16" r="11" stroke="#44403c" strokeWidth="1"/><circle cx="16" cy="16" r="7.5" stroke="#57534e" strokeWidth="0.7"/><polygon points="16,6 17.5,13 16,12 14.5,13" fill="#ffffff"/><polygon points="16,26 14.5,19 16,20 17.5,19" fill="#78716c"/><path d="M16,5 A11,11 0 1,0 16,27" stroke="#f5f5f4" strokeWidth="1.8" strokeLinecap="round" fill="none"/></svg></span>
            CoachGuiden
          </Link>
          <Link href="/ressurser" className="text-sm text-stone-500 hover:text-stone-900">← Alle kalkulatorer</Link>
        </div>
      </header>

      <main className="max-w-2xl mx-auto px-4 py-10">
        <nav className="text-xs text-stone-400 mb-6 flex items-center gap-1">
          <Link href="/" className="hover:text-stone-600">Hjem</Link><span>›</span>
          <Link href="/ressurser" className="hover:text-stone-600">Ressurser</Link><span>›</span>
          <span className="text-stone-600">Stresstest</span>
        </nav>

        <h1 className="text-3xl font-semibold text-stone-900 mb-2">Stresstest — er du i faresonen for utbrenthet?</h1>
        <p className="text-stone-500 mb-6 leading-relaxed">10 spørsmål basert på kliniske indikatorer. Tar 2 minutter. Resultat og konkrete tiltak vises umiddelbart.</p>
        <div className="rounded-3xl overflow-hidden shadow-xl shadow-stone-200/60 aspect-[16/9] mb-8">
          {/* eslint-disable-next-line @next/next/no-img-element */}
          <img src="/images/tools/stresstest.jpg" alt="Stresstest" className="w-full h-full object-cover" loading="eager"/>
        </div>

        <Stresstest />

        <div className="mt-10 space-y-6">
          {/* Source box */}
          <div className="bg-white border border-stone-200 rounded-2xl p-6">
            <h2 className="font-semibold text-stone-900 mb-4">Kilde og beregningsmetode</h2>
            <div className="space-y-3 text-sm text-stone-600">
              <div className="flex gap-3"><span className="text-stone-400 w-28 flex-shrink-0">Grunnlag</span><span>Spørsmålene er basert på Maslach Burnout Inventory (MBI) og Copenhagen Burnout Inventory (CBI) — de mest brukte kliniske instrumentene for å måle utbrenthet.</span></div>
              <div className="flex gap-3"><span className="text-stone-400 w-28 flex-shrink-0">Kilde</span><span>Maslach C, Jackson SE (1981). MBI: Maslach Burnout Inventory. Palo Alto: Consulting Psychologists Press. WHO ICD-11: Burn-out som et yrkesfenomen (QD85).</span></div>
              <div className="flex gap-3"><span className="text-stone-400 w-28 flex-shrink-0">Tolkning</span><span>Score under 25%: lavt stressnivå. 25–50%: moderat. 50–75%: høyt. Over 75%: fare for utbrenthet. Tersklene er basert på klinisk forskning.</span></div>
              <div className="bg-stone-50 rounded-xl p-3 text-xs text-stone-500">Denne testen er et screening-verktøy og erstatter ikke en klinisk vurdering. Hvis du scorer høyt, anbefales det å snakke med lege, psykolog eller mental helse-coach.</div>
            </div>
          </div>

          {/* Internal links */}
          <div className="bg-stone-50 border border-stone-200 rounded-2xl p-5">
            <h3 className="font-medium text-stone-900 mb-3">Relaterte ressurser</h3>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
              {[
                { href: "/mental-helse", title: "Finn mental helse coach", desc: "Støtte for stress og utbrenthet", color: "bg-violet-50 border-violet-200 text-violet-700" },
                { href: "/ressurser/artikler/utbrenthet-symptomer", title: "Utbrenthet: 12 tegn du bør kjenne", desc: "Les vår artikkel om symptomer", color: "bg-violet-50 border-violet-200 text-violet-700" },
                { href: "/ressurser/artikler/sove-bedre", title: "Sove bedre: 8 teknikker", desc: "Evidensbaserte søvntips", color: "bg-stone-100 border-stone-200 text-stone-700" },
                { href: "/", title: "Finn din coach gratis", desc: "Match med riktig coach for deg", color: "bg-stone-900 border-stone-900 text-white" },
              ].map((l: any) => (
                <a key={l.href} href={l.href} className={"flex items-center gap-3 border rounded-xl p-3 hover:shadow-sm transition-all " + l.color}>
                  <div><div className="text-sm font-medium">{l.title}</div><div className="text-xs opacity-70">{l.desc}</div></div>
                  <span className="ml-auto text-xs">→</span>
                </a>
              ))}
            </div>
          </div>

          <div>
            <h2 className="text-xl font-semibold text-stone-900 mb-3">Hva er utbrenthet?</h2>
            <p className="text-stone-600 text-sm leading-relaxed">WHO definerer utbrenthet som et syndrom som oppstår ved kronisk arbeidsrelatert stress som ikke er håndtert. Det kjennetegnes av utmattelse, økt distanse til jobben og redusert profesjonell effektivitet. Det er ikke det samme som å være sliten etter en krevende periode — det er en vedvarende tilstand.</p>
          </div>
          <div>
            <h2 className="text-xl font-semibold text-stone-900 mb-3">Hva gjør du hvis du scorer høyt?</h2>
            <p className="text-stone-600 text-sm leading-relaxed">Ikke ignorer det. Utbrenthet forverres over tid uten intervensjon. Første steg er å snakke med lege eller en mental helse coach som kan hjelpe deg identifisere årsaker og bygge konkrete strategier.</p>
          </div>
        </div>
        <div className="max-w-3xl mx-auto px-6 pb-12">
          <ShareBar label="Del denne testen" />
        </div>
      </main>
    </div>
  );
}
